import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, FileText, Package, PiggyBank, Receipt, ShoppingCart, Sparkles, TrendingUp } from "lucide-react";
import { api, fmtDate, fmtTL, ORDER_STATUS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export default function Dashboard() {
    const { user } = useAuth();
    const { setAiOpen } = useShop();
    const [data, setData] = useState(null);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {});
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const kpis = data?.kpis;
    const isBuyer = user?.role !== "supplier";

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-terra-600">Günaydın</p>
                    <h1 className="font-display text-3xl font-bold tracking-tight">{user?.name}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{user?.company?.name} {user?.supplier ? `• ${user.supplier.name}` : ""}</p>
                </div>
                <Button onClick={() => setAiOpen(true)} variant="outline" className="border-terra-500/40 text-terra-600 hover:bg-terra-600/10" data-testid="dashboard-ai-button">
                    <Sparkles className="mr-2 h-4 w-4" /> AI'a Sor
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[
                    { label: isBuyer ? "Toplam Harcama" : "Toplam Satış", value: fmtTL(kpis?.total_spend), icon: TrendingUp, tid: "kpi-spend" },
                    { label: isBuyer ? "Açık Sipariş" : "Bekleyen Sipariş", value: kpis?.open_orders ?? "—", icon: Package, tid: "kpi-open-orders" },
                    { label: isBuyer ? "Açık Teklif Talebi" : "Bekleyen RFQ", value: isBuyer ? kpis?.open_rfqs ?? 0 : kpis?.pending_quotes ?? 0, icon: FileText, tid: "kpi-rfq" },
                    { label: isBuyer ? "Tahmini Tasarruf" : "Tahsilat Bekleyen", value: fmtTL(isBuyer ? kpis?.savings : kpis?.unpaid_total), icon: isBuyer ? PiggyBank : Receipt, tid: "kpi-savings" },
                ].map((k, i) => (
                    <div key={k.tid} className={`animate-fade-up rounded-2xl border bg-card p-6 shadow-sm`} style={{ animationDelay: `${i * 0.06}s` }} data-testid={k.tid}>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k.label}</p>
                            <k.icon className="h-4 w-4 text-terra-500" />
                        </div>
                        <p className="mt-3 font-display text-2xl font-bold">{k.value}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-7" data-testid="dashboard-monthly-chart">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold">{isBuyer ? "Aylık Satınalma" : "Aylık Satış"}</h2>
                        <Link to="/panel/raporlar" className="text-xs font-semibold text-primary hover:underline">Raporlar →</Link>
                    </div>
                    {data?.monthly?.length ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <AreaChart data={data.monthly}>
                                <defs>
                                    <linearGradient id="spend" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#D96B27" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#D96B27" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
                                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#68615A" />
                                <YAxis tick={{ fontSize: 11 }} stroke="#68615A" tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5DCD3" }} />
                                <Area type="monotone" dataKey="spend" stroke="#D96B27" strokeWidth={2.5} fill="url(#spend)" name="Tutar" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-16 text-center text-sm text-muted-foreground">Henüz veri yok. İlk siparişinizi oluşturun.</p>
                    )}
                </div>

                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-5" data-testid="dashboard-recent-orders">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold">Son Siparişler</h2>
                        <Link to="/panel/siparisler" className="text-xs font-semibold text-primary hover:underline">Tümü →</Link>
                    </div>
                    <div className="space-y-3">
                        {(data?.recent_orders || []).map((o) => (
                            <Link key={o.order_id} to={`/panel/siparis/${o.order_id}`} className="flex items-center justify-between rounded-xl border p-3 transition-colors hover:border-terra-500/40" data-testid={`recent-order-${o.order_no}`}>
                                <div>
                                    <p className="text-sm font-semibold">{o.order_no}</p>
                                    <p className="text-xs text-muted-foreground">{isBuyer ? o.supplier_name : o.buyer_company_name} • {fmtDate(o.created_at)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold">{fmtTL(o.total)}</p>
                                    <Badge variant="outline" className={`text-[10px] ${ORDER_STATUS[o.status]?.cls}`}>{ORDER_STATUS[o.status]?.label}</Badge>
                                </div>
                            </Link>
                        ))}
                        {!data?.recent_orders?.length && <p className="py-10 text-center text-sm text-muted-foreground">Sipariş yok.</p>}
                    </div>
                </div>
            </div>

            {user?.role !== "supplier" && (
                <div data-testid="dashboard-categories">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold">Kategoriye Göre Alışverişe Başla</h2>
                        <Link to="/panel/katalog" className="text-xs font-semibold text-primary hover:underline">Katalog →</Link>
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                        {categories.map((c) => (
                            <Link key={c.category_id} to={`/panel/katalog?kategori=${c.category_id}`} className="group relative overflow-hidden rounded-2xl border" data-testid={`dash-cat-${c.slug}`}>
                                <img src={c.image} alt={c.name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 to-transparent" />
                                <div className="absolute bottom-0 p-3">
                                    <p className="font-display text-sm font-bold text-white leading-tight">{c.name}</p>
                                    <p className="text-[10px] text-stone-300">{c.product_count} ürün</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {user?.role !== "supplier" && (
                <div className="flex flex-wrap gap-3" data-testid="dashboard-quick-actions">
                    <Button asChild className="bg-terra-600 hover:bg-terra-700 text-white"><Link to="/panel/katalog" data-testid="quick-catalog"><ShoppingCart className="mr-2 h-4 w-4" /> Alışverişe Başla</Link></Button>
                    <Button asChild variant="outline"><Link to="/panel/teklifler" data-testid="quick-rfq">Toplu Teklif Talebi <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                </div>
            )}
        </div>
    );
}
